import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from '@at.govt.nz/buttons';
import AddressSearch from '../AddressSearch';
import {
  logEventJourneyPlanAddressSelect,
  logJourneyPlannerEvent,
  JOURNEY_PLAN_TYPE,
  JOURNEY_PLANNER_CATEGORIES,
  JOURNEY_PLANNER_ACTION,
} from '../../utils/GALogger';
import { getJourneyPlannerUrlParams } from '../../utils/UrlGenerator';
import Label from '../ATSearch/Label';

function JourneyPlanner({ jpUrl, jpPreviewOn }) {
  const [fromAddress, setFromAddress] = useState(null);
  const [fromText, setFromText] = useState('');
  const [toAddress, setToAddress] = useState(null);
  const [toText, setToText] = useState('');
  const [formIsValid, setFormIsValid] = useState(true);

  const setAddress = (type, data) => {
    if (!data || data.error) {
      return;
    }

    logEventJourneyPlanAddressSelect(
      data,
      type === 'from' ? JOURNEY_PLAN_TYPE.ORIGIN : JOURNEY_PLAN_TYPE.DEST,
    );

    if (type === 'from') {
      setFromAddress(data);
      setFromText(data.displayText ?? data.address);
    } else {
      setToAddress(data);
      setToText(data.displayText ?? data.address);
    }
  };

  const setFrom = useCallback((text) => {
    setFromText(text);
    setFromAddress(null);
  }, []);

  const setTo = useCallback((text) => {
    setToText(text);
    setToAddress(null);
  }, []);

  const swapFromTo = () => {
    setFromAddress(toAddress);
    setFromText(toText);
    setToAddress(fromAddress);
    setToText(fromText);
  };

  const gotoJourneyPlannerPage = useCallback(() => {
    const journeyPlannerLink = `${jpUrl}?${getJourneyPlannerUrlParams(fromAddress, toAddress, jpPreviewOn)}`;

    logJourneyPlannerEvent(
      JOURNEY_PLANNER_CATEGORIES.NAVIGATION,
      JOURNEY_PLANNER_ACTION.SHOW_EXT_PROVIDERS,
    );

    window.location.href = journeyPlannerLink;
  }, [jpUrl, fromAddress, toAddress]);

  const planJourneyClicked = () => {
    const validationPassed = fromAddress && toAddress;

    if (!validationPassed) {
      setFormIsValid(validationPassed);
      return;
    }

    gotoJourneyPlannerPage();
    setFormIsValid(validationPassed);
  };

  const _handleOnFromAddressDetermined = useCallback((data) => setAddress('from', data), []);
  const _handleOnToAddressDetermined = useCallback((data) => setAddress('to', data), []);

  const errorForStartingPoint = !formIsValid && !fromAddress;
  const errorForDestination = !formIsValid && !toAddress;

  return (
    <div className="at-flex-grid journeyplanner">
      <div className="row">
        <div className="col-xs-12 form">
          <div className="form__inputs at-d-flex at-flex-lg-row at-flex-column at-align-items-lg-start at-me-7">
            <div className="at-d-block">
              <Label htmlFor="fromAddress" text="Starting point / Wāhi tīmata" className="at-font__headline6" showError={errorForStartingPoint} errorText="Enter a starting point">
                <AddressSearch
                  id="fromAddress"
                  ariaLabel="Starting point"
                  address={fromAddress}
                  value={fromText}
                  storageKey="journeyPlan"
                  hasFrontSpace
                  hasCurrentLocation={!!navigator.geolocation}
                  onTextChange={setFrom}
                  onAddressDetermined={_handleOnFromAddressDetermined}
                />
              </Label>
            </div>
            <div className="at-p-2">
              {/* there’s a sonar issue related to this */}
              <div tabIndex="0" className="form__swapButton at-px-2 at-py-2" onClick={swapFromTo} onKeyPress={swapFromTo} role="button" aria-label="Swap starting point and destination" title="Swap starting point and destination">
                <svg aria-label="selected" className="at-icon at-color--shade-shore-20 at-d-none at-d-lg-block"><use href="#icon-transfer" /></svg>
                <svg aria-label="selected" className="at-icon at-color--shade-shore-20 at-d-lg-none"><use href="#icon-switch" /></svg>
              </div>
            </div>
            <div className="at-d-block at-mt-4 at-my-lg-0">
              <Label htmlFor="toAddress" text="Destination / Haerenga" className="at-font__headline6 at-mt-4" showError={errorForDestination} errorText="Enter a destination">
                <AddressSearch
                  id="toAddress"
                  ariaLabel="Destination"
                  address={toAddress}
                  value={toText}
                  storageKey="journeyPlan"
                  hasFrontSpace
                  hasCurrentLocation={!!navigator.geolocation}
                  onTextChange={setTo}
                  onAddressDetermined={_handleOnToAddressDetermined}
                />
              </Label>
            </div>
            <div className="form__circles">
              <div className="form__circles__circle"/>
              <div className="form__circles__circle form__circles__circle--dark"/>
              <div className="form__circles__line"/>
            </div>
            <div className="row">
              <div className="col-xs-12 submit-button-wrapper at-ms-lg-6">
                <Button onClick={planJourneyClicked} className="plan-journey-button at-background-color--shade-shore-20 at-color--white" block>
                  Plan journey
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

JourneyPlanner.propTypes = {
  jpUrl: PropTypes.string.isRequired,
  jpPreviewOn: PropTypes.bool,
};

JourneyPlanner.defaultProps = {
  jpPreviewOn: false,
};

export default JourneyPlanner;
